import type { PowerEnvYearConfig } from '../lib/envSchemeRegistry';
import { DEFAULT_TERM_BUCKETS } from '../lib/termLabel';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type SysConfigEntry = {
  config_id: string;
  name: string;
  value: Record<string, unknown>;
  description: string | null;
  created_at: string;
  updated_at: string;
};

// Year-wise: displayKey (e.g. "LREC", "VEEC/ESS/REES") -> { "2026": 16.67, ... }. The sys_config
// row's `value` shape is `{ schemes: PowerEnvironmentConfig }` — see usePowerEnvDefaults.ts and
// SystemConfig.tsx for read/write. Use resolvePowerPct() from envSchemeRegistry.ts to read a
// value back out, never index this directly (it needs the "nearest configured year" fallback).
export type PowerEnvironmentConfig = PowerEnvYearConfig;

const DEFAULT_REQUESTED_CONTRACT_TERMS = DEFAULT_TERM_BUCKETS;

// Simple in-process cache — mirrors the backend sysConfigService pattern — so components
// that group by nearest term (CompareAndSelectStep, OfferDetailsStep) don't refetch on
// every render.
let _requestedTermsCache: number[] | null = null;
let _requestedTermsCachedAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': 'scopingai',
});

export const sysConfigApiService = {
  async list(): Promise<{ success: boolean; data?: SysConfigEntry[]; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/sys-config`, { headers: getHeaders() });
      const json = await res.json();
      return json;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load config' };
    }
  },

  async get(name: string): Promise<{ success: boolean; data?: SysConfigEntry; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/sys-config/${name}`, { headers: getHeaders() });
      const json = await res.json();
      return json;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load config' };
    }
  },

  /**
   * Standard contract term lengths (months) offers are requested at. Used to group
   * retailer-returned non-standard terms (e.g. 13, 25 months) under the nearest one
   * instead of each odd value getting its own comparison-table column.
   * Falls back to DEFAULT_TERM_BUCKETS if the config row doesn't exist or the request fails.
   */
  async getRequestedContractTerms(): Promise<number[]> {
    const now = Date.now();
    if (_requestedTermsCache && now - _requestedTermsCachedAt < CACHE_TTL_MS) {
      return _requestedTermsCache;
    }

    const result = await sysConfigApiService.get('requested_contract_terms');
    const v = result.data?.value as { terms?: unknown } | undefined;
    const terms = Array.isArray(v?.terms)
      ? (v!.terms as unknown[]).map(Number).filter(t => !isNaN(t) && t > 0).sort((a, b) => a - b)
      : [];

    _requestedTermsCache = terms.length > 0 ? terms : DEFAULT_REQUESTED_CONTRACT_TERMS;
    _requestedTermsCachedAt = now;
    return _requestedTermsCache;
  },

  async upsert(
    name: string,
    value: Record<string, unknown>,
    description?: string
  ): Promise<{ success: boolean; data?: SysConfigEntry; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/sys-config/${name}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ value, description }),
      });
      const json = await res.json();
      return json;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to save config' };
    }
  },
};
